/**
 * Fixed, collapsible left sidebar (Atlas pattern): brand mark at the top, then
 * the navigation entries the SERVER permitted. No role check lives here.
 */

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { useIndustries, useNewFeedbackCount } from '@/api/queries';
import {
  GROUP_LABELS,
  NAV_GROUP_ORDER,
  activeNavKey,
  entriesInGroup,
  type NavEntry,
  type NavGroup,
} from '@/navigation/navigation';
import { useOpenSubmitIdea } from '@/routes/business-opportunities/SubmitIdeaDialog';
import { useSession } from '@/session/SessionContext';

import { BrandMark } from './BrandMark';

/** The counts on entries, for those who act on them; zero for everyone else. */
function useNavCounts() {
  const { can } = useSession();
  // Industries awaiting review, counted on the Industries entry for those who review them.
  const industries = useIndustries({ enabled: can('industry.review') });
  const pendingReview = industries.data?.industries.filter((i) => i.status === 'pending').length ?? 0;
  // Feedback nobody has triaged yet, counted on the Feedback entry for those who triage it.
  const newFeedback = useNewFeedbackCount({ enabled: can('feedback.view.any') });
  const counts: Record<string, number> = { industries: pendingReview, feedback: newFeedback };
  return (key: string) => counts[key] ?? 0;
}

const countNoun = (key: string) => (key === 'feedback' ? 'new' : 'pending review');

/** A dialog entry: only Submit an idea today. It opens over Business Opportunities, where the new idea will be listed. */
function DialogEntry({ entry, label }: { entry: NavEntry; label: string }) {
  const navigate = useNavigate();
  const openSubmitIdea = useOpenSubmitIdea();
  const Icon = entry.icon;
  return (
    <button
      type="button"
      className="nav-link"
      data-label={label}
      onClick={() => {
        navigate('/business-opportunities');
        openSubmitIdea();
      }}
    >
      <span className="nav-icon">
        <Icon aria-hidden="true" />
      </span>
      <span className="nav-label">{entry.label}</span>
    </button>
  );
}

function LinkEntry({ entry, to, label, count, active }: { entry: NavEntry; to: string; label: string; count: number; active: boolean }) {
  const Icon = entry.icon;
  const counted = count > 0;
  return (
    <NavLink
      to={to}
      data-label={label}
      aria-label={counted ? label : undefined}
      className={() => (active ? 'nav-link is-active' : 'nav-link')}
    >
      <span className="nav-icon">
        <Icon aria-hidden="true" />
        {counted && (
          <span className="nav-count-bubble badge-warning" aria-hidden="true">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
      <span className="nav-label">{entry.label}</span>
      {counted && (
        <span className="badge badge-warning nav-count" aria-hidden="true">
          {count}
        </span>
      )}
    </NavLink>
  );
}

function NavGroupSection({
  group,
  activeKey,
  countFor,
}: {
  group: NavGroup;
  activeKey: string | undefined;
  countFor: (key: string) => number;
}) {
  const { canSee } = useSession();
  const visible = entriesInGroup(group).filter((entry) => canSee(entry.key));
  if (visible.length === 0) return null;
  return (
    <div>
      {group !== null && <div className="nav-group-label">{GROUP_LABELS[group] ?? group}</div>}
      {visible.map((entry) => {
        const count = countFor(entry.key);
        const label = count > 0 ? `${entry.label}, ${count} ${countNoun(entry.key)}` : entry.label;
        if (entry.path === null) return <DialogEntry key={entry.key} entry={entry} label={label} />;
        return (
          <LinkEntry key={entry.key} entry={entry} to={entry.path} label={label} count={count} active={activeKey === entry.key} />
        );
      })}
    </div>
  );
}

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { pathname } = useLocation();
  const countFor = useNavCounts();
  const activeKey = activeNavKey(pathname);
  const toggleLabel = collapsed ? 'Expand navigation' : 'Collapse navigation';

  return (
    <aside className="app-sidebar" aria-label="Main navigation">
      <div className="app-brand">
        <span className="app-brand-mark">
          <BrandMark />
        </span>
        <span className="app-brand-text">
          <span className="app-wordmark">Wynxx</span>
          <span className="app-wordmark app-wordmark-light">Discovery</span>
        </span>
        <button type="button" className="icon-btn nav-collapse-btn" onClick={onToggle} aria-label={toggleLabel} title={toggleLabel}>
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
        </button>
      </div>

      <nav className="app-nav">
        {NAV_GROUP_ORDER.map((group) => (
          <NavGroupSection key={group ?? 'ungrouped'} group={group} activeKey={activeKey} countFor={countFor} />
        ))}
      </nav>
    </aside>
  );
}
